<script setup lang="ts">
/**
 * MeasuresEditView – Takt-Editor „Takt einfügen/löschen" im NoteActionsOverlay.
 *
 * Bewusst ein EIGENER Reiter neben „Wiederholungen & Taktstriche" (`StructureEditView`): das
 * Einfügen/Löschen ganzer Takte ist eine Struktur-Operation am Stück, keine Eigenschaft eines
 * Taktstrichs. Der Zustand (`measureCount` für den Lösch-Guard) kommt reaktiv aus
 * `currentMeasureStructure`.
 */
import {computed} from 'vue';
import {Icon} from '@iconify/vue';
import {injectScoreEditContext} from '@/components/musicxml-preview/editContext.ts';

const {
  currentMeasureStructure,
  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  insertMeasure,
  deleteMeasure,
} = injectScoreEditContext();

const measureCount = computed(() => currentMeasureStructure.value?.measureCount ?? 0);

const BUTTON_CLASS = 'flex-1 rounded-lg border px-1 py-1.5 text-sm font-semibold transition-colors';
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="measureIndex !== null"
        class="flex items-center justify-center p-2 overflow-auto"
    >
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

        <!-- Header -->
        <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-orange-50">
          <div>
            <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
              <div class="p-2 bg-rose-100 text-rose-600 rounded-lg">
                <Icon icon="fa6-solid:table-cells-large" class="text-lg"/>
              </div>
              Takt bearbeiten
            </h2>
            <p class="text-sm text-gray-600 mt-1">Takt {{ measureNumber }} ({{ measureIndex + 1 }} von {{ measureCount }})</p>
          </div>
        </div>

        <!-- Content -->
        <div class="p-5 space-y-2">
          <label class="block text-sm font-medium text-gray-700">Takt einfügen</label>
          <div class="flex gap-1">
            <button
                type="button"
                :class="[BUTTON_CLASS, 'btn-glow-green-400']"
                title="Leeren Takt vor diesem einfügen"
                @click="insertMeasure('before')"
            >
              <Icon icon="streamline-flex:track-select-right-tool-remix" class="mr-1 inline rotate-180"/>
              davor
            </button>
            <button
                type="button"
                :class="[BUTTON_CLASS, 'btn-glow-green-400']"
                title="Leeren Takt nach diesem einfügen"
                @click="insertMeasure('after')"
            >

              <Icon icon="streamline-flex:track-select-right-tool-remix" class="mr-1 inline"/>
              danach
            </button>
          </div>
          <!--          <label class="block pt-2 text-sm font-medium text-gray-700">Takt entfernen</label>-->
          <button
              type="button"
              :class="[BUTTON_CLASS, 'w-full btn-danger mt-2']"
              :disabled="measureCount <= 1"
              title="Diesen Takt score-weit löschen (Rückgängig über die Verlaufsleiste)"
              @click="deleteMeasure"
          >
            <Icon icon="fa6-solid:trash" class="mr-1 inline"/>
            Takt löschen
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
